/**
 * AnalysisResultsListPage Component
 * Page displaying all user's analysis requests with filtering, sorting, and pagination
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DashboardHeader } from '@/components/layout/DashboardHeader';
import { Footer } from '@/components/layout/Footer';
import { RequestCard } from '@/components/dashboard/RequestCard';
import { Button } from '@/components/ui/button';
import { useFilteredProcessingRequests, useProcessingRequests } from '@/hooks/useAnalyses';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { ProcessingStatus } from '@/types/api';
import { cn } from '@/lib/utils';

const ITEMS_PER_PAGE = 9; // 3 columns x 3 rows

/**
 * Status filter options
 */
interface StatusOption {
  value: number | null;
  labelKey: string;
}

const statusOptions: StatusOption[] = [
  { value: null, labelKey: 'analysisResultsList.allStatuses' },
  { value: ProcessingStatus.Pending, labelKey: 'requestCard.statusPending' },
  { value: ProcessingStatus.InProgress, labelKey: 'requestCard.statusInProgress' },
  { value: ProcessingStatus.Completed, labelKey: 'requestCard.statusCompleted' },
  { value: ProcessingStatus.Failed, labelKey: 'requestCard.statusFailed' },
];

/**
 * Sort order options
 */
interface SortOption {
  value: 'desc' | 'asc';
  labelKey: string;
}

const sortOptions: SortOption[] = [
  { value: 'desc', labelKey: 'analysisResultsList.newestFirst' },
  { value: 'asc', labelKey: 'analysisResultsList.oldestFirst' },
];

/**
 * Empty state component when user has no analyses at all
 */
function NoAnalysesState() {
  const { t } = useTranslation();
  const openAnalysisDialog = useAnalysisDialogStore((s) => s.open);

  return (
    <div className="space-y-8 py-16">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-4 text-[var(--color-charcoal)]">
          {t('analysisResultsList.noResultsTitle')}
        </h2>
        <p className="text-[var(--color-charcoal)]/70">
          {t('analysisResultsList.noResultsDescription')}
        </p>
      </div>
      <div className="text-center">
        <Button size="lg" data-track="results_empty_new_analysis" className="btn-primary" onClick={openAnalysisDialog}>
          {t('welcome.ctaButton')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Empty state component when filter returns no results
 */
function NoFilterResultsState({ onClearFilters }: { onClearFilters: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
        <Search className="w-8 h-8 text-gray-400" />
      </div>
      <h2 className="text-xl font-semibold text-[var(--color-charcoal)] mb-3">
        {t('analysisResultsList.noFilterResultsTitle')}
      </h2>
      <p className="text-[var(--color-charcoal)]/70 mb-6">
        {t('analysisResultsList.noFilterResultsDescription')}
      </p>
      <Button
        variant="outline"
        onClick={onClearFilters}
      >
        {t('analysisResultsList.clearFilters')}
      </Button>
    </div>
  );
}

/**
 * Filter dropdown component
 */
function FilterSelect<T extends string | number | null>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; labelKey: string }[];
  onChange: (value: T) => void;
  label: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-[var(--color-charcoal)]/70">
        {label}
      </label>
      <select
        value={value === null ? 'null' : String(value)}
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'null') {
            onChange(null as T);
          } else if (!isNaN(Number(val))) {
            onChange(Number(val) as T);
          } else {
            onChange(val as T);
          }
        }}
        className={cn(
          'px-3 py-2 rounded-lg border border-gray-200',
          'bg-white text-[var(--color-charcoal)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-start)]/20 focus:border-[var(--color-primary-start)]',
          'transition-colors cursor-pointer',
          'min-w-[160px]'
        )}
      >
        {options.map((option) => (
          <option
            key={option.value === null ? 'null' : String(option.value)}
            value={option.value === null ? 'null' : String(option.value)}
          >
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Pagination component
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const pages = useMemo(() => {
    const result: (number | 'ellipsis')[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        result.push(i);
      }
    } else {
      result.push(1);

      if (currentPage > 3) {
        result.push('ellipsis');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        result.push(i);
      }

      if (currentPage < totalPages - 2) {
        result.push('ellipsis');
      }

      result.push(totalPages);
    }

    return result;
  }, [currentPage, totalPages]);

  return (
    <nav
      className="flex items-center justify-center gap-2 mt-8"
      aria-label={t('analysisResultsList.pagination')}
    >
      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label={t('analysisResultsList.previousPage')}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      <div className="flex items-center gap-1">
        {pages.map((page, index) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-[var(--color-charcoal)]/50"
            >
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onPageChange(page)}
              className={cn(
                'min-w-[36px]',
                page === currentPage && 'btn-primary'
              )}
            >
              {page}
            </Button>
          )
        )}
      </div>

      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label={t('analysisResultsList.nextPage')}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </nav>
  );
}

/**
 * Analysis results list page
 */
export default function AnalysisResultsListPage() {
  const { t } = useTranslation();

  // Filter and pagination state
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch all requests (unfiltered) to check loading state
  const { isLoading: isLoadingAll } = useProcessingRequests();

  // Fetch filtered requests
  const { requests: apiRequests, isLoading, total } = useFilteredProcessingRequests({
    page,
    pageSize: ITEMS_PER_PAGE,
    status: statusFilter,
    sortOrder,
  });

  // Use API requests directly (no mock data)
  const requests = apiRequests;

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  // Check if user has any analyses
  const hasAnyAnalyses = total > 0 || requests.length > 0;

  // Reset to page 1 when filters change
  const handleStatusChange = (value: number | null) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleSortChange = (value: 'asc' | 'desc') => {
    setSortOrder(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter(null);
    setPage(1);
  };

  const combinedLoading = isLoading || isLoadingAll;

  return (
    <DashboardLayout header={<DashboardHeader />} footer={<Footer />}>
      <div className="max-w-4xl mx-auto">
        {/* Page header + filters */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <h1 className="gradient-text text-3xl md:text-4xl font-bold leading-tight">
            {t('analysisResultsList.title')}
          </h1>

          {(hasAnyAnalyses || combinedLoading) && (
            <div className="flex flex-wrap items-end gap-3">
              <FilterSelect
                value={statusFilter}
                options={statusOptions}
                onChange={handleStatusChange}
                label={t('analysisResultsList.filterByStatus')}
              />
              <FilterSelect
                value={sortOrder}
                options={sortOptions}
                onChange={handleSortChange}
                label={t('analysisResultsList.sortByDate')}
              />
            </div>
          )}
        </div>

        {/* Content */}
        {combinedLoading ? (
          // Loading skeleton
          <div
            className="grid gap-6 grid-cols-1 md:grid-cols-3"
            role="list"
            aria-label={t('analysisResultsList.loadingLabel')}
          >
            {Array.from({ length: 9 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="h-[140px] rounded-lg bg-gray-100 animate-pulse"
                role="listitem"
              />
            ))}
          </div>
        ) : !hasAnyAnalyses ? (
          // User has no analyses at all
          <NoAnalysesState />
        ) : requests.length > 0 ? (
          <>
            {/* Results grid - 3 columns to match dashboard width */}
            <div
              className="grid gap-6 grid-cols-1 md:grid-cols-3"
              role="list"
              aria-label={t('analysisResultsList.resultsLabel')}
            >
              {requests.map((request) => (
                <div key={request.id} role="listitem">
                  <RequestCard request={request} />
                </div>
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        ) : (
          // Filter returned no results
          <NoFilterResultsState onClearFilters={handleClearFilters} />
        )}
      </div>
    </DashboardLayout>
  );
}
